import { apiClient } from '../utils/api';

export type ActStatus = 'PLANNED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

export type ContractStatus = 'DRAFT' | 'PENDING' | 'SIGNED' | 'EXPIRED' | 'CANCELLED';

export interface ActDocument {
  id: string;
  contractId: string;
  createdAt: string;
  hasPdf: boolean;
  hasDocx: boolean;
}

export type ServiceCategory = 'TATTOO' | 'PIERCING' | 'JEWELRY' | 'AFTERCARE' | 'CONSULTATION' | 'REMOVAL';

export interface Act {
  id: string;
  appointmentId?: string | null;
  serviceId: string;
  serviceCategory?: ServiceCategory | null;
  practitionerId: string;
  shopId: string;
  clientId?: string | null;
  sequence?: number | null;
  durationMinutes?: number | null;
  plannedStartAt?: string | null;
  plannedEndAt?: string | null;
  demandDetails?: Record<string, any> | null;
  status: ActStatus;
  notes?: string | null;
  performedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  contractId?: string | null;
  contractStatus?: ContractStatus | null;
  contractType?: string | null;
  contractSigned?: boolean;
  documents?: ActDocument[];
  documentsCount?: number;
  piercingRecord?: {
    id: string;
    piercingType: string;
    piercingTypeModelId?: string | null;
    bodyZoneId?: string | null;
    isHealed: boolean;
    jewelry?: unknown;
    needleGauge?: string | null;
    placement?: unknown;
    bodyZone?: { id: string; name: string } | null;
    piercingTypeModel?: { id: string; name: string; basePrice?: number | null } | null;
  } | null;
  tattooRecord?: { id: string; style?: string | null; colorType?: string | null; isCompleted: boolean } | null;
}

export interface CreateActData {
  appointmentId?: string | null;
  serviceId: string;
  practitionerId: string;
  shopId: string;
  clientId?: string | null;
  sequence?: number;
  durationMinutes?: number;
  plannedStartAt?: string | null;
  plannedEndAt?: string | null;
  status?: ActStatus;
  notes?: string | null;
  performedAt?: string | null;
}

export interface UpdateActData extends Partial<CreateActData> {}

export interface GetActsParams {
  appointmentId?: string;
  practitionerId?: string;
  shopId?: string;
  clientId?: string;
  status?: ActStatus;
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'performedAt' | 'status' | 'plannedStartAt';
  sortDir?: 'asc' | 'desc';
  from?: string;
  to?: string;
}

export interface GetActsResult {
  acts: Act[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

export async function getActs(params: GetActsParams = {}): Promise<GetActsResult> {
  const queryParams = new URLSearchParams();
  if (params.appointmentId) queryParams.append('appointmentId', params.appointmentId);
  if (params.practitionerId) queryParams.append('practitionerId', params.practitionerId);
  if (params.shopId) queryParams.append('shopId', params.shopId);
  if (params.clientId) queryParams.append('clientId', params.clientId);
  if (params.status) queryParams.append('status', params.status);
  if (params.page) queryParams.append('page', String(params.page));
  if (params.limit) queryParams.append('limit', String(params.limit));
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortDir) queryParams.append('sortDir', params.sortDir);
  if (params.from) queryParams.append('from', params.from);
  if (params.to) queryParams.append('to', params.to);

  const response = await apiClient(`/api/planning/acts?${queryParams}`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to fetch acts');
  }

  const data: ApiResponse<GetActsResult> = await response.json();
  if (!data.success) {
    throw new Error(data.message || 'Failed to fetch acts');
  }

  return data.data ?? { acts: [], total: 0, page: 1, limit: 50, totalPages: 1 };
}

export async function createAct(payload: CreateActData): Promise<Act> {
  const response = await apiClient('/api/planning/acts', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to create act');
  }

  const data: ApiResponse<{ act: Act }> = await response.json();
  if (!data.success || !data.data?.act) {
    throw new Error(data.message || 'Failed to create act');
  }

  return data.data.act;
}

export async function updateAct(id: string, payload: UpdateActData): Promise<Act> {
  const response = await apiClient(`/api/planning/acts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to update act');
  }

  const data: ApiResponse<{ act: Act }> = await response.json();
  if (!data.success || !data.data?.act) {
    throw new Error(data.message || 'Failed to update act');
  }

  return data.data.act;
}

export async function deleteAct(id: string, options?: { hardDelete?: boolean }): Promise<void> {
  const query = options?.hardDelete ? '?hardDelete=true' : '';
  const response = await apiClient(`/api/planning/acts/${id}${query}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to delete act');
  }

  const data: ApiResponse<void> = await response.json();
  if (!data.success) {
    throw new Error(data.message || 'Failed to delete act');
  }
}
